/**
 * Quản lý trạng thái phiên làm việc: hồ sơ người dùng, quyền, phạm vi địa bàn.
 *
 * Phạm vi địa bàn là NGỮ CẢNH MẶC ĐỊNH của toàn hệ thống: mặc định lấy theo phân
 * công của người dùng (`phamViDiaBan` trong hồ sơ), hiển thị ở thanh ngữ cảnh, và
 * mọi màn hình danh sách đọc từ đây. Giao diện KHÔNG phải là nơi bảo đảm an toàn:
 * máy chủ đã cắt kết quả theo phạm vi, nên sửa tham số trên đường dẫn cũng không
 * lấy được dữ liệu ngoài phạm vi.
 *
 * Tài khoản cấp xã (hợp đồng A0 tài khoản xã mục 6): phạm vi KHOÁ vào đúng xã được
 * phân công — `phamViDangChon` luôn là xã đó, `datPhamViDangChon` không có tác dụng,
 * và `khoaDiaBan` cho các bộ chọn địa bàn biết để hiện tên xã, không cho đổi, không
 * có lựa chọn "Toàn tỉnh".
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { HoSoNguoiDung } from '../api/khach-api';

export interface NguCanhPhien {
  hoSo: HoSoNguoiDung | null;
  /** Có quyền này không? Quản trị nền tảng có mọi quyền. */
  coQuyen: (quyen: string) => boolean;
  /** Phạm vi địa bàn đang chọn; rỗng nghĩa là toàn bộ phạm vi được phân công. */
  phamViDangChon: string[];
  datPhamViDangChon: (ds: string[]) => void;
  /** Phạm vi gốc theo phân công, không đổi trong phiên. */
  phamViDuocPhanCong: string[];
  /** Địa bàn dùng cho tham số `nguonId` của lời gọi API; rỗng là không gửi tham số. */
  nguonIdHienTai: string | undefined;
  /** Tài khoản cấp xã: xã bị khoá (mã + tên). `null` với tài khoản cấp tỉnh. */
  khoaDiaBan: { ma: string; ten: string } | null;
  /** Tên địa bàn được phân công theo mã (từ `tenPhamVi` của hồ sơ). */
  tenDiaBanTheoMa: ReadonlyMap<string, string>;
  /**
   * Thay hồ sơ phiên bằng hồ sơ mới máy chủ trả về (sau PATCH /auth/me): tên đầu
   * trang và mọi nơi đọc `hoSo` đổi theo ngay. Không có nơi giữ hồ sơ thì không làm gì.
   */
  capNhatHoSo: (hoSo: HoSoNguoiDung) => void;
}

/**
 * Xã bị khoá của một hồ sơ: chỉ khi `cap = 'xa'` và có đúng một mã phạm vi.
 * Thiếu tên (API cũ) thì dùng tạm mã.
 */
export function xaCuaHoSo(hoSo: HoSoNguoiDung | null | undefined): { ma: string; ten: string } | null {
  if (!hoSo || hoSo.cap !== 'xa') return null;
  const ma = hoSo.phamViDiaBan[0];
  if (!ma) return null;
  return { ma, ten: hoSo.tenPhamVi?.[0] || ma };
}

const MacDinh: NguCanhPhien = {
  hoSo: null,
  coQuyen: () => true,
  phamViDangChon: [],
  datPhamViDangChon: () => undefined,
  phamViDuocPhanCong: [],
  nguonIdHienTai: undefined,
  khoaDiaBan: null,
  tenDiaBanTheoMa: new Map(),
  capNhatHoSo: () => undefined,
};

const Boi = createContext<NguCanhPhien>(MacDinh);

export function dungPhien(): NguCanhPhien {
  return useContext(Boi);
}

export interface NhaCungCapPhienProps {
  hoSo?: HoSoNguoiDung | null;
  /** Nơi giữ hồ sơ (App) nhận hồ sơ mới; vắng thì phiên tự giữ bản đã sửa. */
  onCapNhatHoSo?: (hoSo: HoSoNguoiDung) => void;
  children: ReactNode;
}

export function NhaCungCapPhien({
  hoSo: hoSoProp,
  onCapNhatHoSo,
  children,
}: NhaCungCapPhienProps): ReactNode {
  // Bản hồ sơ đã sửa tại chỗ (khi không có `onCapNhatHoSo`, vd. kiểm thử); prop đổi thì bỏ.
  const [hoSoDaSua, datHoSoDaSua] = useState<{
    goc: HoSoNguoiDung | null | undefined;
    moi: HoSoNguoiDung;
  } | null>(null);
  const hoSo = hoSoDaSua && hoSoDaSua.goc === hoSoProp ? hoSoDaSua.moi : hoSoProp;
  const capNhatHoSo = useCallback(
    (moi: HoSoNguoiDung): void => {
      if (onCapNhatHoSo) onCapNhatHoSo(moi);
      else datHoSoDaSua({ goc: hoSoProp, moi });
    },
    [onCapNhatHoSo, hoSoProp],
  );
  const phanCong = useMemo(() => hoSo?.phamViDiaBan ?? [], [hoSo]);
  const [phamViDangChon, datPhamViDangChon] = useState<string[]>([]);

  const coQuyen = useCallback(
    (quyen: string): boolean => {
      // Không có hồ sơ nghĩa là đang ở chế độ khung (kiểm thử đợt 0): không lọc.
      if (!hoSo) return true;
      if (hoSo.laQuanTriNenTang) return true;
      return (hoSo.quyen ?? []).includes(quyen);
    },
    [hoSo],
  );

  const tenDiaBanTheoMa = useMemo(() => {
    const m = new Map<string, string>();
    hoSo?.phamViDiaBan.forEach((ma, i) => {
      const ten = hoSo.tenPhamVi?.[i];
      if (ten) m.set(ma, ten);
    });
    return m;
  }, [hoSo]);

  const giaTri = useMemo<NguCanhPhien>(() => {
    const khoaDiaBan = xaCuaHoSo(hoSo);
    if (khoaDiaBan) {
      // Tài khoản xã: phạm vi cố định, không đổi được trong phiên.
      return {
        hoSo: hoSo ?? null,
        coQuyen,
        phamViDangChon: [khoaDiaBan.ma],
        datPhamViDangChon: () => undefined,
        phamViDuocPhanCong: [khoaDiaBan.ma],
        nguonIdHienTai: khoaDiaBan.ma,
        khoaDiaBan,
        tenDiaBanTheoMa,
        capNhatHoSo,
      };
    }
    const dangChon = phamViDangChon.length > 0 ? phamViDangChon : phanCong;
    return {
      hoSo: hoSo ?? null,
      coQuyen,
      phamViDangChon,
      datPhamViDangChon,
      phamViDuocPhanCong: phanCong,
      // API nhận một mã địa bàn; chọn nhiều nút thì gửi nút chung nhất là mã tỉnh.
      nguonIdHienTai: dangChon.length === 1 ? dangChon[0] : undefined,
      khoaDiaBan: null,
      tenDiaBanTheoMa,
      capNhatHoSo,
    };
  }, [hoSo, coQuyen, phamViDangChon, phanCong, tenDiaBanTheoMa, capNhatHoSo]);

  return <Boi.Provider value={giaTri}>{children}</Boi.Provider>;
}
